import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import QuestionMark from 'components/widgets/QuestionMark';
import { DEFAULT_SITE_ICON } from '../constants';
import { getPlatformName } from '../utils';
import css from './Controls.css';

const PLATFORM_ICON_PATHS = {
    android: '/static/dashboard/img/platforms/android_icon.png',
    ios: '/static/dashboard/img/platforms/ios_icon.png',
    iphone: '/static/dashboard/img/platforms/iphone_icon.png',
    ipad: '/static/dashboard/img/platforms/ipad_icon.png',
    web: '/static/dashboard/img/platforms/web_platform.svg',
    pc: '/static/dashboard/img/platforms/pc_platform_blue.svg',
    xbox: '/static/dashboard/img/platforms/xbox_platform_blue.svg',
    playstation: '/static/dashboard/img/platforms/playstation_platform_blue.svg',
    nintendo: '/static/dashboard/img/platforms/nintendo_platform_blue.svg',
    metaquest: '/static/dashboard/img/platforms/meta_quest_platform_blue.svg',
    ctv: '/static/dashboard/img/platforms/tv_platform_blue.svg',
    other: '/static/dashboard/img/platforms/other_platform_blue.svg',
};

const getPlatformIcon = siteOrPlatform =>
    PLATFORM_ICON_PATHS[getPlatformName(siteOrPlatform)] || PLATFORM_ICON_PATHS.other;

export const HelpLabel = ({ id, tooltip = null }) => (
    <span className={css.helpLabel}>
        <Translate id={id} />
        {tooltip ? <QuestionMark message={tooltip} containerClassName={css.questionMark} interactive /> : null}
    </span>
);

HelpLabel.propTypes = {
    id: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
};

export const AppIcon = ({ src }) => {
    const [iconSrc, setIconSrc] = useState(src || DEFAULT_SITE_ICON);

    useEffect(() => {
        setIconSrc(src || DEFAULT_SITE_ICON);
    }, [src]);

    return <img className={css.appIcon} src={iconSrc} alt="" onError={() => setIconSrc(DEFAULT_SITE_ICON)} />;
};

AppIcon.propTypes = {
    src: PropTypes.string,
};

export const SiteStatusIcon = ({ integrated }) => (
    <span className={classNames(css.sdkIcon, { [css.sdkIconOk]: integrated, [css.sdkIconFail]: !integrated })}>
        {integrated ? '✓' : '×'}
    </span>
);

SiteStatusIcon.propTypes = {
    integrated: PropTypes.bool,
};

export const PlatformIcon = ({ site, muted = false, variant = 'default' }) => (
    <span
        className={classNames(css.platformIconWrap, {
            [css.platformIconMuted]: muted,
            [css.platformIconBlue]: variant === 'blue',
        })}
    >
        <img className={css.platformIcon} src={getPlatformIcon(site)} alt="" />
        {site?.is_live_in_store ? <span className={css.platformLiveDot} /> : null}
    </span>
);

PlatformIcon.propTypes = {
    muted: PropTypes.bool,
    site: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    variant: PropTypes.oneOf(['blue', 'default']),
};

export const Field = ({
    label,
    value,
    placeholder = '',
    disabled = false,
    error = null,
    onBlur = () => {},
    onChange = () => {},
    type = 'text',
    className = '',
    maxLength = undefined,
    minLength = undefined,
}) => {
    const inputId = useId();

    return (
        <label className={classNames(css.formField, className)} htmlFor={inputId}>
            <span>{label}</span>
            <input
                id={inputId}
                type={type}
                value={value || ''}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
                minLength={minLength}
                onBlur={onBlur}
                onChange={event => onChange(event.target.value)}
            />
            {error ? <span className={css.fieldError}>{error}</span> : null}
        </label>
    );
};

Field.propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    label: PropTypes.node.isRequired,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const InlineField = ({
    label,
    value,
    placeholder = '',
    disabled = false,
    error = null,
    onBlur = () => {},
    onChange = () => {},
    type = 'text',
    maxLength = undefined,
}) => {
    const inputId = useId();

    return (
        <label className={css.inlineFieldRow} htmlFor={inputId}>
            <div className={css.inlineFieldLabel}>{label}</div>
            <div className={css.inlineFieldControl}>
                <input
                    id={inputId}
                    type={type}
                    value={value || ''}
                    placeholder={placeholder}
                    disabled={disabled}
                    maxLength={maxLength}
                    onBlur={onBlur}
                    onChange={event => onChange(event.target.value)}
                />
                {error ? <span className={css.fieldError}>{error}</span> : null}
            </div>
        </label>
    );
};

InlineField.propTypes = {
    disabled: PropTypes.bool,
    error: PropTypes.string,
    label: PropTypes.node.isRequired,
    maxLength: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const StaticMetric = ({ label, value }) => (
    <div className={css.staticMetric}>
        <div>{label}</div>
        <strong title={typeof value === 'string' || typeof value === 'number' ? value : ''}>{value || '-'}</strong>
    </div>
);

StaticMetric.propTypes = {
    label: PropTypes.node.isRequired,
    value: PropTypes.node,
};

export const SwitchLine = ({
    checked,
    label,
    onChange = () => {},
    disabled = false,
    className = '',
    withRule = false,
}) => {
    const labelId = useId();

    return (
        <div className={classNames(css.switchLine, { [css.switchLineWithRule]: withRule }, className)}>
            <button
                type="button"
                className={classNames(css.switchControl, { [css.switchControlChecked]: checked })}
                role="switch"
                aria-checked={checked}
                aria-labelledby={labelId}
                disabled={disabled}
                onClick={() => onChange(!checked)}
            >
                <span />
            </button>
            <span
                id={labelId}
                className={css.switchLabel}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => {
                    if (!disabled) onChange(!checked);
                }}
                onKeyDown={event => {
                    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                        onChange(!checked);
                    }
                }}
            >
                {label}
            </span>
            {withRule ? <span className={css.switchRule} /> : null}
        </div>
    );
};

SwitchLine.propTypes = {
    checked: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    label: PropTypes.node.isRequired,
    onChange: PropTypes.func,
    withRule: PropTypes.bool,
};

export const DestinationList = ({
    title,
    emptyText,
    destinations = [],
    disabled = false,
    onChange,
    includeDefaultRow = false,
    defaultRowLabel = '',
    defaultRowTitle = '',
}) => {
    const addDestination = () => onChange([...destinations, { url: '' }]);

    return (
        <div className={css.destinationGroup}>
            <div className={css.destinationGroupLabel}>{title}</div>
            <div className={css.destinationRows}>
                {includeDefaultRow ? (
                    <div className={css.destinationRow}>
                        <input
                            value={defaultRowLabel}
                            disabled
                            className={css.destinationDefaultInput}
                            title={defaultRowTitle}
                        />
                        {!disabled && !destinations.length ? (
                            <div className={css.destinationActions}>
                                <button type="button" className={css.addDestinationButton} onClick={addDestination}>
                                    +
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
                {destinations.length ? (
                    destinations.map((destination, index) => (
                        <div key={destination.id || index} className={css.destinationRow}>
                            <input
                                value={destination.url || ''}
                                disabled={disabled}
                                onChange={event => {
                                    const nextDestinations = destinations.map((item, itemIndex) =>
                                        itemIndex === index ? { ...item, url: event.target.value } : item
                                    );
                                    onChange(nextDestinations);
                                }}
                            />
                            {!disabled ? (
                                <div className={css.destinationActions}>
                                    <button
                                        type="button"
                                        className={css.inlineIconButton}
                                        onClick={() =>
                                            onChange(destinations.filter((_, itemIndex) => itemIndex !== index))
                                        }
                                    >
                                        ×
                                    </button>
                                    {index === destinations.length - 1 ? (
                                        <button
                                            type="button"
                                            className={css.addDestinationButton}
                                            onClick={addDestination}
                                        >
                                            +
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ))
                ) : !includeDefaultRow ? (
                    <div className={css.destinationEmptyRow}>
                        {!disabled ? (
                            <button type="button" className={css.emptyDestinationButton} onClick={addDestination}>
                                {emptyText}
                            </button>
                        ) : (
                            <div className={css.mutedLine}>{emptyText}</div>
                        )}
                        {!disabled ? (
                            <div className={css.destinationActions}>
                                <button type="button" className={css.addDestinationButton} onClick={addDestination}>
                                    +
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

DestinationList.propTypes = {
    disabled: PropTypes.bool,
    destinations: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, url: PropTypes.string })),
    defaultRowLabel: PropTypes.string,
    defaultRowTitle: PropTypes.string,
    emptyText: PropTypes.node.isRequired,
    includeDefaultRow: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    title: PropTypes.node.isRequired,
};

export const DiscreteAttributionSlider = ({
    label,
    tooltip,
    options,
    value,
    disabled = false,
    onChange,
    formatValue,
    visibleTickIndices = [],
    leftLabel,
    rightLabel,
    separatorIndex = null,
}) => {
    const resolvedIndex = Math.max(
        0,
        options.findIndex(option => option.value === value)
    );
    const resolvedOption = options[resolvedIndex] || options[0];
    const resolvedLabel = formatValue(resolvedOption?.value || 0);
    const resolvedLeft = `${(resolvedIndex / Math.max(options.length - 1, 1)) * 100}%`;

    return (
        <div className={css.attributionSliderRow} title={resolvedLabel}>
            <div className={css.attributionSliderLabel}>
                <Translate id={label} />
                {tooltip ? <QuestionMark message={tooltip} containerClassName={css.questionMark} interactive /> : null}
            </div>
            <div className={css.attributionSliderControl}>
                {leftLabel || rightLabel ? (
                    <div className={css.attributionSliderHeadings}>
                        <span>{leftLabel}</span>
                        <span>{rightLabel}</span>
                    </div>
                ) : null}
                <div className={css.attributionSliderTrack} title={resolvedLabel}>
                    <div className={css.attributionSliderSelectedValue} style={{ left: resolvedLeft }}>
                        {resolvedLabel}
                    </div>
                    <div className={css.attributionSliderTrackLine} />
                    <div className={css.attributionSliderTrackActive} style={{ width: resolvedLeft }} />
                    {separatorIndex !== null ? (
                        <div
                            className={css.attributionSliderSeparator}
                            style={{ left: `${(separatorIndex / Math.max(options.length - 1, 1)) * 100}%` }}
                        />
                    ) : null}
                    <input
                        type="range"
                        className={css.attributionSliderInput}
                        value={resolvedIndex}
                        min={0}
                        max={options.length - 1}
                        step={1}
                        disabled={disabled}
                        onChange={event => onChange(options[Number(event.target.value)]?.value ?? options[0].value)}
                    />
                    <div className={css.attributionSliderMarker} style={{ left: resolvedLeft }} />
                </div>
                <div className={css.attributionSliderTicks}>
                    {visibleTickIndices.map(index => {
                        const option = options[index];
                        if (!option) {
                            return null;
                        }

                        return (
                            <button
                                key={option.value}
                                type="button"
                                className={classNames(css.attributionSliderTick, {
                                    [css.attributionSliderTickActive]: index === resolvedIndex,
                                })}
                                style={{ left: `${(index / Math.max(options.length - 1, 1)) * 100}%` }}
                                disabled={disabled}
                                onClick={() => onChange(option.value)}
                            >
                                {formatValue(option.value)}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

DiscreteAttributionSlider.propTypes = {
    disabled: PropTypes.bool,
    formatValue: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    leftLabel: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number })).isRequired,
    rightLabel: PropTypes.string,
    separatorIndex: PropTypes.number,
    tooltip: PropTypes.string,
    value: PropTypes.number.isRequired,
    visibleTickIndices: PropTypes.arrayOf(PropTypes.number),
};
